import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import EkselensMark from "../components/EkselensMark";

const QUICK_LINKS = [
  { to: "/services", label: "Services", desc: "12 consulting services across the EKSELENS360 lifecycle." },
  { to: "/resources", label: "Resources", desc: "Downloadable diagnostics and frameworks." },
  { to: "/insights", label: "Insights", desc: "Sharp commentary on transformation, AI and delivery." },
  { to: "/contact", label: "Contact", desc: "Book a free 30-minute discovery." },
];

const NotFound = () => {
  const location = useLocation();
  return (
    <section
      className="min-h-[80vh] ek-gradient-dark text-white relative overflow-hidden"
      data-testid="not-found-page"
    >
      <div className="ek-star-watermark" />
      <div className="ek-vignette-top" />
      <div className="ek-container relative z-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <EkselensMark size={56} loading="eager" />
            <div className="text-[11px] font-semibold tracking-[0.22em] text-[#A179FA] uppercase mt-8">
              404 — Page not found
            </div>
            <h1
              className="ek-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4 leading-tight"
              data-testid="not-found-headline"
            >
              That page has moved <br /> or doesn't exist.
            </h1>
            <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-8 max-w-2xl">
              The URL <code className="text-[#A179FA] bg-white/5 px-2 py-0.5 text-[14px]">{location.pathname}</code>{" "}
              didn't match anything on the Ekselens site. Use the links opposite to find your way back, or jump straight to a discovery conversation.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/" className="ek-btn ek-btn-primary" data-testid="404-home-link">
                <Compass size={16} /> Back to home
              </Link>
              <Link to="/contact" className="ek-btn ek-btn-outline-light" data-testid="404-contact-link">
                Book a discovery <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-2">
            <div className="ek-eyebrow text-[#A179FA]">Where to next</div>
            {QUICK_LINKS.map((q) => (
              <Link
                key={q.to}
                to={q.to}
                className="block bg-white/[0.03] hover:bg-white/[0.07] border border-white/10 hover:border-white/30 p-5 transition-colors group"
                data-testid={`404-quick-${q.label.toLowerCase()}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="ek-heading text-base text-white">{q.label}</span>
                  <ArrowRight size={16} className="text-zinc-500 group-hover:text-[#A179FA] transition-colors" />
                </div>
                <p className="text-zinc-400 text-[13.5px] mt-1.5 leading-relaxed">{q.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NotFound;
